import { useCallback, useState } from 'react';

export type UseGalleryStateReturn = {
  readonly selectedIdx: number;
  readonly modalOpen: boolean;
  readonly modalSelectedIdx: number;
  readonly setSelectedIdx: (idx: number) => void;
  readonly setModalSelectedIdx: (idx: number) => void;
  readonly handleShowModal: (idx: number) => void;
  readonly handleCloseModal: () => void;
  readonly goToPrev: (maxIdx: number) => void;
  readonly goToNext: (maxIdx: number) => void;
};

/**
 * Manages the UI state for a media gallery:
 * - Selected image/video index
 * - Modal open/closed state
 * - Modal selected index
 * - Prev/next navigation with wraparound
 */
export const useGalleryState = (): UseGalleryStateReturn => {
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalSelectedIdx, setModalSelectedIdx] = useState(0);

  const handleShowModal = useCallback((idx: number) => {
    setModalSelectedIdx(idx);
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  const goToPrev = useCallback((maxIdx: number) => {
    setSelectedIdx((idx) => {
      if (idx - 1 < 0) return maxIdx;
      return Math.max(idx - 1, 0);
    });
  }, []);

  const goToNext = useCallback((maxIdx: number) => {
    if (maxIdx < 0) return;

    setSelectedIdx((idx) => {
      if (idx + 1 > maxIdx) return 0;
      return idx + 1;
    });
  }, []);

  return {
    selectedIdx,
    modalOpen,
    modalSelectedIdx,
    setSelectedIdx,
    setModalSelectedIdx,
    handleShowModal,
    handleCloseModal,
    goToPrev,
    goToNext,
  };
};
